import React, {useState} from "react";
import {createUseStyles, useTheme} from "react-jss";
import {Virtuoso} from "react-virtuoso";
import {HostLobby, ServerUser, TeamMode} from "engage-common/types";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import clsx from "clsx";
import RadioGroup, {Radio} from "engage-client/components/RadioGroup";
import {DefaultTeamExplanation, TeamInfo} from "engage-client/components/teams/CreateTeams";
import DoubleArrowIcon from "engage-client/components/icons/DoubleArrowIcon";
import {Theme} from "engage-client/theme";


const useStyles = createUseStyles<Theme>((theme) => ({
    userTabContainer: {
        flex: 1,
        position: "relative"
    },
    contentScroller: {
        //Remove it from the layout so that it doesn't force it's parent to grow
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,

        display:"flex",
        flexDirection: "column",
        overflowY: "auto"
    },
    label: {
        marginTop: 16,
        marginBottom: 6,
    },
    explanation: {
        flex: "0 0 75px",
        margin: [5,10,20],
        alignSelf: "stretch",
        backgroundColor: "#D2DAE0",
        border: "none",
        borderBottom: `1px solid ${theme.color.primary.main}`,
        fontFamily: '"Anova", "Roboto", "Helvetica", "Arial", "sans-serif"',
        fontSize: 16,
        padding: 10,
        color: theme.color.primary.main,
        resize: "none",
    },
}));

type TeamsUsersProps = {
    hostLobby: HostLobby
}

export default function TeamsUsers({hostLobby}:TeamsUsersProps) {
    const users = Object.values(hostLobby.users);
    const theme = useTheme<Theme>();
    const classes = useStyles();
    const connectedUsers = users.filter(user => user.connected).length;
    return (
        <div className={classes.userTabContainer} data-qa="user-list">

                <div className={classes.contentScroller}>
                    <DisplayText variant={TextVariant.s18med} color="primary"
                                 data-qa="user-connected" data-value={connectedUsers}>
                        {connectedUsers} Players Connected
                    </DisplayText>
                    {
                        hostLobby.teamMode === TeamMode.Off ?
                            <UserList users={users}
                                      title="Users"
                                      height={500}
                                      teamInfo={theme.teamInfos["first"]}
                            />
                            :
                            <>
                                <DisplayText variant={TextVariant.s16reg} className={classes.label}>Team Assignment:</DisplayText>
                                <RadioGroup name="teamMode" value={hostLobby.teamMode} disabled={true}>
                                    <Radio value={TeamMode.Choose}
                                           labelText="Player chooses their team"
                                           labelVariant={TextVariant.s16reg}/>
                                    <Radio value={TeamMode.Random}
                                           labelText="Randomly assign a team"
                                           labelVariant={TextVariant.s16reg}/>
                                </RadioGroup>
                                <DisplayText variant={TextVariant.s16reg} className={classes.label}>Explanation:</DisplayText>
                                <textarea className={classes.explanation}
                                          placeholder={DefaultTeamExplanation}
                                          value={hostLobby.teamExplanation} disabled={true} />
                                {hostLobby.teams?.map(team =>
                                    <UserList users={users.filter(u=>u.team===team.key)}
                                              title={team.name}
                                              height={300}
                                              teamInfo={theme.teamInfos[team.key]}
                                              key={team.key}
                                    />
                                )}
                            </>
                    }
                </div>

        </div>
    );
};

type UserListProps = {
    users: ServerUser[]
    height: number
    title: string
    teamInfo: TeamInfo
}

const useListStyles = createUseStyles({
    userList: {
        marginRight: 10,
        marginTop: 20,
        borderBottom: props => props.collapsed ? "none" : `1px solid ${props.highlightColor}`,
    },
    topBar: {
        display: "flex",
    },
    connectedNumber: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 10,
        backgroundColor: props => props.highlightColor,
    },
    title: {
        backgroundColor: props => props.backgroundColor,
        border: props => `1px dashed ${props.highlightColor}`,
        color: props => props.highlightColor,
        flex: 1,
        padding: 10,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
    },
    userRow: {
        display: "flex",
        alignItems: "center",
        padding: [10,0],
        "&.highlighted": {
            backgroundColor: props => props.backgroundColor
        }
    },
    userNickname: {
        marginRight: 20,
    },
    status: {
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "#AAAAAA",
        margin: [0, 5],
        "&.connected": {
            backgroundColor: "#42AF09"
        },
    },
    collapseIcon: {
        transform: props => `scaleY(${props.collapsed ? -1 : 1})`,
        transition: "transform 250ms ease-in-out"
    }
});

function UserList(props:UserListProps) {
    const {users, height, teamInfo, title} = props;
    const [collapsed, setCollapsed] = useState(true);
    const classes = useListStyles({
        highlightColor: teamInfo.main,
        backgroundColor: teamInfo.lightBG,
        collapsed
    });

    function renderRow(index:number) {
        const user = users[index];
        return (
            <div key={index} data-qa="user-row" className={clsx(classes.userRow, index%2 === 0 && "highlighted")}>
                <div className={clsx(classes.status, user.connected && "connected")}/>
                <DisplayText variant={TextVariant.s16reg} fontWeight={400} color="primary"
                             component="span" className={classes.userNickname} data-qa="user-nickname">
                    {user.nickname}
                </DisplayText>
            </div>
        );
    }

    return (
        <div className={classes.userList}>
            <div className={classes.topBar}>
                <DisplayText variant={TextVariant.s18med}
                             className={classes.connectedNumber}
                             color="white">
                    {users.length}
                </DisplayText>
                <div className={classes.title} onClick={()=>setCollapsed(current=>!current)}>
                    <DisplayText variant={TextVariant.s18med}>
                        {title}
                    </DisplayText>
                    <DoubleArrowIcon className={classes.collapseIcon} color={teamInfo.main}/>
                </div>
            </div>
            <Virtuoso style={{width:"100%", height: collapsed ? 0 : height}}
                      totalCount={users.length}
                      item={renderRow}/>
        </div>

    );
}